import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Globe, 
  TrendingDown, 
  ShieldCheck, 
  Package, 
  Clock,
  HeadphonesIcon,
  CheckCircle,
  Star
} from 'lucide-react';

const WhyChooseUsPage = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'International Supplier Network',
      description: 'Access to a vast network of vetted suppliers across Europe, Asia, and beyond. We leverage our relationships to secure the best products at competitive prices.',
      stats: '500+ verified suppliers',
    },
    {
      icon: TrendingDown,
      title: 'Cost Optimization',
      description: 'Our strategic sourcing approach and negotiation expertise consistently deliver significant cost savings without compromising on quality.',
      stats: '20-30% average savings',
    },
    {
      icon: ShieldCheck,
      title: 'DRC & African Market Expertise',
      description: 'Deep understanding of DRC, Central African business landscapes, logistics challenges, and regulatory requirements ensures smooth operations.',
      stats: 'Local expertise',
    },
    {
      icon: Package,
      title: 'Complete Process Management',
      description: 'From initial requirement analysis to final delivery, we manage every step of the procurement process with precision.',
      stats: 'End-to-end service',
    },
    {
      icon: Clock,
      title: 'Time Efficiency',
      description: 'Our established processes and supplier relationships significantly reduce procurement timelines and administrative burden.',
      stats: '50% faster procurement',
    },
    {
      icon: HeadphonesIcon,
      title: 'Dedicated Support',
      description: 'Each client benefits from a dedicated account manager who ensures personalized service and quick response times.',
      stats: '24/7 availability',
    },
  ];

  const testimonials = [
    {
      quote: "Supply Hub has transformed our procurement process. Their expertise in African markets and global sourcing has reduced our costs by 25%.",
      author: "Jean-Pierre Koffi",
      role: "Operations Director",
      company: "West African Manufacturing Co.",
    },
    {
      quote: "Reliable, efficient, and professional. They understand our industry requirements and consistently deliver quality products on time.",
      author: "Sarah Okonkwo",
      role: "Procurement Manager",
      company: "Lagos Construction Group",
    },
    {
      quote: "Working with Supply Hub has given us access to suppliers we never knew existed. Their network is truly impressive.",
      author: "Michel Dubois",
      role: "Supply Chain Director",
      company: "Abidjan Agro Industries",
    },
  ];

  const guarantees = [
    'Quality assurance on all products',
    'Transparent pricing with no hidden fees',
    'Timely delivery with real-time tracking',
    'Compliance with international standards',
    'Flexible payment terms',
    'Risk mitigation strategies',
  ];

  return (
    <div data-testid="why-us-page">
      {/* Hero Section */}
      <section className="relative bg-[#0F172A] pt-32 pb-20 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Why Choose Us
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Your Strategic Advantage in Procurement
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Discover why leading businesses across Africa trust Supply Hub as their 
              procurement partner.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section data-testid="benefits-section" className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Our Advantages
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-4 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Strategic advantages that deliver measurable results for your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                data-testid={`benefit-detail-${index}`}
                className="group bg-white border border-slate-200 p-8 hover:border-[#0EA5E9] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center mb-6 group-hover:bg-[#0EA5E9] transition-colors">
                  <benefit.icon className="text-white" size={28} />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {benefit.description}
                </p>
                <div className="pt-4 border-t border-slate-100">
                  <span className="font-['JetBrains_Mono'] text-[#0EA5E9] text-sm font-medium">
                    {benefit.stats}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section data-testid="guarantees-section" className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
                Our Promise
              </span>
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-4 mb-6">
                Our Commitment to You
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We stand behind our services with clear commitments that protect your 
                interests and ensure satisfaction.
              </p>
              <ul className="space-y-4">
                {guarantees.map((guarantee, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-[#0EA5E9] flex-shrink-0" size={20} />
                    <span className="text-slate-700">{guarantee}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 border border-slate-200 -z-10"></div>
              <img
                src="/4.jpeg"
                alt="Business Partnership"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="bg-[#0F172A] py-20 md:py-32 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Client Success
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-white mt-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.author}
                data-testid={`testimonial-${index}`}
                className="bg-[#1E293B] border border-slate-700 p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-[#F97316] fill-[#F97316]" size={16} />
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-slate-600 pt-4">
                  <p className="font-medium text-white">{testimonial.author}</p>
                  <p className="text-slate-400 text-sm">{testimonial.role}</p>
                  <p className="text-[#0EA5E9] text-sm">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              The Difference
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-4 mb-4">
              Supply Hub vs. Traditional Procurement
            </h2>
          </div>

          <div className="bg-white border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-3 bg-[#0F172A] text-white">
              <div className="p-4 font-medium">Aspect</div>
              <div className="p-4 font-medium text-center">Traditional</div>
              <div className="p-4 font-medium text-center bg-[#0EA5E9]">Supply Hub</div>
            </div>
            {[
              { aspect: 'Supplier Access', traditional: 'Limited', supplyhub: 'Global Network' },
              { aspect: 'Cost Savings', traditional: '5-10%', supplyhub: '20-30%' },
              { aspect: 'Processing Time', traditional: 'Weeks', supplyhub: 'Days' },
              { aspect: 'Quality Assurance', traditional: 'Variable', supplyhub: 'Guaranteed' },
              { aspect: 'Support', traditional: 'Business Hours', supplyhub: '24/7 Dedicated' },
              { aspect: 'Risk Management', traditional: 'Basic', supplyhub: 'Comprehensive' },
            ].map((row, index) => (
              <div key={row.aspect} className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <div className="p-4 font-medium text-[#0F172A]">{row.aspect}</div>
                <div className="p-4 text-center text-slate-500">{row.traditional}</div>
                <div className="p-4 text-center text-[#0EA5E9] font-medium">{row.supplyhub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0EA5E9] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-white mb-4">
            Ready to Experience the Difference?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Join hundreds of satisfied clients who have transformed their procurement with Supply Hub.
          </p>
          <Link to="/contact">
            <Button
              data-testid="why-us-cta-btn"
              className="bg-white text-[#0EA5E9] hover:bg-[#F8FAFC] rounded-none px-8 py-4 font-medium transition-colors duration-300"
            >
              Get Started Today
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUsPage;
